import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldAlert, Send, UserPlus, Check, Loader } from "lucide-react";
import { toast } from "sonner";
import { usePublicOperative } from "../hooks/useOperativeCard";
import { useMemberAuth } from "../hooks/useMemberAuth";
import OperativeCard from "../components/operative-card/OperativeCard";
import ResponsiveCardFrame from "../components/operative-card/ResponsiveCardFrame";
import { operativeNetworkApi } from "../lib/authApi";

/**
 * Public profile — /member/:callsign
 * Only resolves when the member has explicitly opted-in via
 * `public_profile_enabled`. Otherwise 404 (no existence leak).
 *
 * PRIVACY: this page NEVER displays an email address. Member-to-member
 * contact goes through Archive Dispatch via callsign only.
 */
const PublicOperativeProfile = () => {
  const { callsign } = useParams();
  const { card, loading, notFound } = usePublicOperative(callsign);
  const { isMember, member } = useMemberAuth();
  const nav = useNavigate();
  const [addBusy, setAddBusy] = useState(false);
  const [addStatus, setAddStatus] = useState(null); // 'pending' | 'accepted' | null

  const ownProfile = isMember && member?.callsign &&
    member.callsign.toUpperCase() === (callsign || "").toUpperCase();

  const messageOperative = () => {
    if (!isMember) { nav(`/auth/login?next=/member/${callsign}`); return; }
    // Deep-link into the composer by hash so Inbox auto-opens with prefill
    nav(`/inbox?to=${encodeURIComponent(callsign)}`);
  };

  const addToNetwork = async () => {
    if (!isMember) { nav(`/auth/login?next=/member/${callsign}`); return; }
    setAddBusy(true);
    try {
      const { data } = await operativeNetworkApi.request({ callsign });
      setAddStatus(data?.status || "pending");
      if (data?.status === "accepted") {
        toast.success("Operative added to your network.");
      } else {
        toast.success("Add-Operative request sent — pending clearance.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send request.";
      toast.error(msg);
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#08070A" }} data-testid="public-operative-profile">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-8">
          <ArrowLeft size={12} /> Archive Home
        </Link>

        {loading && (
          <div className="text-center py-20">
            <Loader2 size={20} className="animate-spin mx-auto text-[#A39E93]" />
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] mt-3 text-[#6F6A60]">
              LOOKING UP OPERATIVE
            </div>
          </div>
        )}

        {!loading && notFound && (
          <div className="border p-8 text-center max-w-md mx-auto" style={{ borderColor: "#2D2A26" }} data-testid="public-profile-not-found">
            <ShieldAlert size={28} className="mx-auto text-[#D92D20]" />
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20] mt-3">
              // NO RECORD ON FILE
            </div>
            <div className="font-display text-2xl mt-2 text-[#E4DFD3]">
              {callsign?.toUpperCase()}
            </div>
            <p className="text-sm text-[#A39E93] mt-3">
              This operative does not have a public profile. They may not exist,
              or they have chosen to keep their credential private.
            </p>
          </div>
        )}

        {!loading && !notFound && card && (
          <div className="flex flex-col items-center gap-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#9FA8D4]">
              // PUBLIC CREDENTIAL · ISSUED BY THE ARCHIVE
            </div>
            <div style={{ width: "100%", maxWidth: 650 }}>
              <ResponsiveCardFrame data-testid="public-card-frame">
                <OperativeCard card={card} />
              </ResponsiveCardFrame>
            </div>

            {/* One-click operative actions — the point of a profile is
                to enable connection. Both CTAs sit visible above the
                fold on mobile and route through auth when needed. */}
            {!ownProfile && (
              <div className="flex flex-wrap items-center justify-center gap-3 w-full max-w-md"
                   data-testid="public-profile-actions">
                <button
                  type="button"
                  onClick={messageOperative}
                  data-testid="public-profile-message-btn"
                  className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em]"
                  style={{ background: "#D92D20", color: "#fff", border: "1px solid #D92D20" }}
                >
                  <Send size={12} /> SEND DISPATCH
                </button>
                <button
                  type="button"
                  onClick={addToNetwork}
                  disabled={addBusy || addStatus === "pending" || addStatus === "accepted"}
                  data-testid="public-profile-add-btn"
                  className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-60"
                  style={{ border: "1px solid #5DD4B3AA", color: "#5DD4B3" }}
                >
                  {addBusy ? <Loader size={12} className="animate-spin" />
                    : addStatus === "accepted" ? <Check size={12} />
                    : <UserPlus size={12} />}
                  {addBusy ? "SENDING…"
                    : addStatus === "accepted" ? "IN YOUR NETWORK"
                    : addStatus === "pending" ? "PENDING CLEARANCE"
                    : "ADD OPERATIVE"}
                </button>
              </div>
            )}

            <div className="text-center max-w-md">
              <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#6F6A60]">
                Want your own credential?
              </div>
              <Link to="/members" className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1.5"
                    style={{ borderColor: "#D92D20", color: "#D92D20" }}>
                JOIN THE ARCHIVE
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicOperativeProfile;
